import React, { useState } from 'react';
import styled from 'styled-components';
import { FaDownload, FaFileAlt } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ResumeContainer = styled.div`
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
`;

const SectionLabel = styled.div`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.9rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 30px;
  align-self: flex-start;

  .prompt {
    color: var(--accent-cyan, #22d3ee);
    margin-right: 8px;
    font-weight: 600;
  }
`;

const ResumeCard = styled.div`
  width: 100%;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.1);
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: blur(4px);
`;

const ResumePreview = styled.div`
  position: relative;
  width: 100%;
  max-height: 700px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  background: #1e293b;

  /* Style the react-pdf canvas to fit */
  .react-pdf__Document {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .react-pdf__Page {
    max-width: 100%;
  }

  .react-pdf__Page canvas {
    max-width: 100% !important;
    height: auto !important;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(transparent, rgba(30, 41, 59, 1));
    pointer-events: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: var(--text-muted, #64748b);
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
`;

const DownloadSection = styled.div`
  padding: 24px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(148, 163, 184, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: var(--text-muted, #64748b);
  }
`;

const FileName = styled.div`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  color: var(--text-primary, #e2e8f0);

  span {
    display: block;
    font-size: 0.7rem;
    color: var(--text-muted, #64748b);
    margin-top: 2px;
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.85rem;
  color: var(--accent-cyan, #22d3ee);
  background: rgba(34, 211, 238, 0.08);
  border: 1px solid rgba(34, 211, 238, 0.3);
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(34, 211, 238, 0.15);
    border-color: rgba(34, 211, 238, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(34, 211, 238, 0.15);
  }
`;

function Resume() {
  const resumePdfPath = require('../src/Images/resume.pdf');
  const [pageWidth, setPageWidth] = useState(null);

  const onDocumentLoadSuccess = () => {
    // Calculate page width based on container
    const container = document.querySelector('[data-resume-preview]');
    if (container) {
      setPageWidth(Math.min(container.clientWidth - 20, 800));
    }
  };

  return (
    <ResumeContainer id="resume">
      <SectionLabel>
        <span className="prompt">$</span> open resume.pdf
      </SectionLabel>
      <ResumeCard>
        <ResumePreview data-resume-preview>
          <Document
            file={resumePdfPath}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<LoadingContainer>Loading resume...</LoadingContainer>}
            error={<LoadingContainer>Failed to load PDF</LoadingContainer>}
          >
            <Page
              pageNumber={1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </ResumePreview>
        <DownloadSection>
          <FileInfo>
            <FaFileAlt size={20} />
            <FileName>
              Dev_Kansara_Resume.pdf
              <span>Click to download the full PDF</span>
            </FileName>
          </FileInfo>
          <DownloadButton
            href={resumePdfPath}
            download="Dev_Kansara_Resume.pdf"
            title="Download Resume PDF"
          >
            <FaDownload size={14} />
            Download PDF
          </DownloadButton>
        </DownloadSection>
      </ResumeCard>
    </ResumeContainer>
  );
}

export default Resume;